import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import {
  IconButton,
  Skeleton,
  Tabs,
  Tab,
  Box,
  Menu,
  MenuItem,
} from '@mui/material';
import { ArrowBack, MoreVert, Edit } from '@mui/icons-material';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';

import {
  DependabilityCard,
  WorkerDetailsCard,
  OnboardingStepsCard,
  CheckHQCard,
  ZealCard,
  CheckrCard,
  WorkerSkillsCard,
  PermanentSuspendModal,
  SignedDocumentsCard,
} from './components';
import CommentsCard from './components/CommentsCard';
import UpdateProfileModal from './components/UpdateProfileModal';
import ChangeAvatarWrapper from './components/ChangeAvatarWrapper';
import ResetPasswordModal from './components/ResetPasswordModal';
import ChangeAddressModal from './components/ChangeAddressModal';
import DeactivateWorkerModal from './components/DeactivateWorkerModal';
import ViolationsModal from './components/ViolationsModal';
import JobsListCard from '../Worker/WorkerCompleted/JobListCard';

import Avatar from '@/components/Avatar';
import Alert from '@/components/Alert';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Card from '@/elements/Card';
import Page from '@/components/Page';

import Stack from '@/components/Stack';
import { Body, Small, Title } from '@/components/Typography';
import WorkerSuspensionBanner from '@/components/WorkerSuspensionBanner';
import {
  WorkersV2API,
  WorkerDetail,
  ExternalsResponse,
  DependabilityScore,
} from '@/services/workersV2API';
import { accountabilityAPI } from '@/services/accountabilityAPI';
import LiftSuspensionModal from './components/LiftSuspensionModal';
import {
  getAccountStatusBadgeStatus,
  getAccountStatusLabel,
  getSuspensionStatusBadgeStatus,
  getSuspensionStatusLabel,
} from '@/util/workerStatus';

const STACK_GAP = 24;

const PageSkeleton = () => (
  <Page
    title={
      <Stack vertical gap={8}>
        <Skeleton height={32} width={200} />
        <Skeleton height={20} width={150} />
      </Stack>
    }
  >
    <div
      style={{
        display: 'grid',
        gap: '24px',
        gridTemplateAreas: '"primary secondary"',
        gridTemplateRows: 'auto',
        gridTemplateColumns: '1fr 320px',
      }}
    >
      <div style={{ gridArea: 'primary' }}>
        <Stack vertical gap={STACK_GAP}>
          <Skeleton height={300} variant="rounded" />
          <Skeleton height={400} variant="rounded" />
        </Stack>
      </div>
      <div style={{ gridArea: 'secondary' }}>
        <Stack vertical gap={STACK_GAP}>
          <Skeleton height={500} variant="rounded" />
          <Skeleton height={300} variant="rounded" />
        </Stack>
      </div>
    </div>
  </Page>
);

const WorkerV2 = () => {
  const { workerId } = useParams<{ workerId: string }>();
  const navigate = useNavigate();

  const [worker, setWorker] = useState<WorkerDetail | null>(null);
  const [externals, setExternals] = useState<ExternalsResponse | null>(null);
  const [dependabilityScore, setDependabilityScore] =
    useState<DependabilityScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showLiftSuspensionModal, setShowLiftSuspensionModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [liftingSuspension, setLiftingSuspension] = useState(false);
  const [reactivatingWorker, setReactivatingWorker] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [canUnsuspend, setCanUnsuspend] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showViolationsModal, setShowViolationsModal] = useState(false);
  const [showResolvePrompt, setShowResolvePrompt] = useState(false);
  const [resolvingViolations, setResolvingViolations] = useState(false);

  const fetchWorkerData = useCallback(async () => {
    if (!workerId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch worker details and externals in parallel
      const [workerResponse, externalsResponse] = await Promise.all([
        WorkersV2API.getWorker(parseInt(workerId)),
        WorkersV2API.getWorkerExternals(parseInt(workerId)),
      ]);

      if (workerResponse.success) {
        setWorker(workerResponse.data);
      } else {
        setError('Failed to fetch worker details');
      }

      if (externalsResponse.success) {
        setExternals(externalsResponse);
      }

      // Fetch accountability separately so it doesn't block the page
      WorkersV2API.getWorkerAccountability(parseInt(workerId))
        .then((res) => {
          if (res.success && res.data.dependability_score) {
            setDependabilityScore(res.data.dependability_score);
          }
        })
        .catch(() => {});

      // Fetch violation approver permission for unsuspend gate
      accountabilityAPI
        .getClearPermission()
        .then((res) => {
          setCanUnsuspend(!!(res.success && res.can_clear_violation));
        })
        .catch(() => {});
    } catch (err) {
      console.error('Error fetching worker data:', err);
      setError('Failed to fetch worker data');
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  useEffect(() => {
    fetchWorkerData();
  }, [fetchWorkerData]);

  const handleBack = () => {
    navigate('/workers_v2');
  };

  const handleSyncComplete = () => {
    fetchWorkerData();
  };

  const handleShareLink = () => {
    const url = `${window.location.origin}/workers_v2/${workerId}`;
    navigator.clipboard.writeText(url);
    setActionSuccess('Link copied to clipboard');
    setMenuAnchor(null);
  };

  const handleOpenLiftSuspensionModal = () => {
    setActionError(null);
    setActionSuccess(null);
    setShowLiftSuspensionModal(true);
  };

  const handleOpenReactivateModal = () => {
    setActionError(null);
    setActionSuccess(null);
    setShowReactivateModal(true);
  };

  const handleOpenDeactivateModal = () => {
    setActionError(null);
    setActionSuccess(null);
    setShowDeactivateModal(true);
    setMenuAnchor(null);
  };

  const getErrorMessage = (err: unknown, fallback: string) => {
    const responseData = (
      err as {
        response?: {
          data?: {
            message?: string;
            errors?: string[];
          };
        };
      }
    )?.response?.data;
    if (responseData?.message) return responseData.message as string;
    if (
      Array.isArray(responseData?.errors) &&
      (responseData.errors as string[]).length > 0
    ) {
      return (responseData.errors as string[]).join(', ');
    }

    if (err instanceof Error && err.message) return err.message;
    return fallback;
  };

  const handleLiftSuspension = async (reason: string) => {
    if (!worker?.active_suspension?.id) {
      throw new Error('No active suspension found for this worker');
    }

    setLiftingSuspension(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      const response = await WorkersV2API.liftSuspension(
        worker.active_suspension.id,
        reason,
      );

      if (!response.success) {
        throw new Error(
          response.message ||
            response.errors?.join(', ') ||
            'Failed to lift suspension',
        );
      }

      await fetchWorkerData();
      setActionSuccess('Worker suspension lifted successfully');
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err, 'Failed to lift suspension'));
    } finally {
      setLiftingSuspension(false);
    }
  };

  const handleReactivateWorker = async () => {
    if (!worker) {
      throw new Error('Worker not found');
    }

    setReactivatingWorker(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      const response = await WorkersV2API.reactivateWorker(worker.id);

      if (!response.success) {
        throw new Error(
          response.errors?.join(', ') || 'Failed to reactivate worker',
        );
      }

      await fetchWorkerData();
      setActionSuccess('Worker reactivated successfully');

      // Check if worker has active violations and prompt to resolve
      const accountabilityRes = await WorkersV2API.getWorkerAccountability(
        worker.id,
      );
      const activeViolations =
        (accountabilityRes.data?.statistics?.total_violations ?? 0) -
        (accountabilityRes.data?.statistics?.total_appeals ?? 0);
      if (activeViolations > 0) {
        setShowResolvePrompt(true);
      }
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err, 'Failed to reactivate worker'));
    } finally {
      setReactivatingWorker(false);
    }
  };

  const handleBulkResolveViolations = async () => {
    if (!worker) return;
    setResolvingViolations(true);
    try {
      const response = await accountabilityAPI.bulkResolveViolations(
        worker.id,
        'Resolved on re-enable',
      );
      if (response.success) {
        setActionSuccess(
          `${response.data?.resolved_count ?? 0} violation(s) resolved`,
        );
        await fetchWorkerData();
      } else {
        setActionError('Failed to resolve violations');
      }
    } catch {
      setActionError('Failed to resolve violations');
    } finally {
      setResolvingViolations(false);
      setShowResolvePrompt(false);
    }
  };

  const handleDeactivateWorker = async (reason: string) => {
    if (!worker) {
      throw new Error('Worker not found');
    }

    setActionError(null);
    setActionSuccess(null);

    try {
      const response = await WorkersV2API.deactivateWorker(worker.id, reason);

      if (!response.success) {
        throw new Error(
          response.errors?.join(', ') || 'Failed to deactivate worker',
        );
      }

      await fetchWorkerData();
      setActionSuccess('Worker deactivated successfully');
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err, 'Failed to deactivate worker'));
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (error || !worker) {
    return (
      <Page title="Worker Not Found">
        <Stack
          vertical
          gap={16}
          style={{ padding: '40px', alignItems: 'center' }}
        >
          <Body color="lighter">{error || 'Worker not found'}</Body>
          <IconButton onClick={handleBack}>
            <ArrowBack /> Back to Workers
          </IconButton>
        </Stack>
      </Page>
    );
  }

  const workerName = worker.user?.full_name || 'N/A';

  return (
    <Page size="xl">
      {showLiftSuspensionModal && (
        <LiftSuspensionModal
          workerName={workerName}
          onClose={() => setShowLiftSuspensionModal(false)}
          onSubmit={handleLiftSuspension}
        />
      )}
      {showReactivateModal && (
        <ConfirmationModal
          acceptAction={handleReactivateWorker}
          acceptButtonLabel="Confirm Reactivate"
          bodyContentText={`You are about to reactivate ${workerName}. This will restore access to the platform.`}
          denyAction={() => {}}
          denyButtonLabel="Cancel"
          hideModal={() => setShowReactivateModal(false)}
          title="Reactivate Worker Account"
          loading={reactivatingWorker}
        />
      )}
      {showDeactivateModal && (
        <DeactivateWorkerModal
          workerName={workerName}
          onClose={() => setShowDeactivateModal(false)}
          onSubmit={handleDeactivateWorker}
        />
      )}
      {showProfileModal && (
        <UpdateProfileModal
          worker={worker}
          onClose={() => setShowProfileModal(false)}
          onSuccess={fetchWorkerData}
        />
      )}
      {showAvatarModal && (
        <ChangeAvatarWrapper
          workerId={String(worker.id)}
          currentAvatarUrl={worker.avatar_url}
          onClose={() => setShowAvatarModal(false)}
          onSuccess={() => {
            setShowAvatarModal(false);
            fetchWorkerData();
          }}
        />
      )}
      {showResetPasswordModal && (
        <ResetPasswordModal
          workerId={String(worker.id)}
          onClose={() => setShowResetPasswordModal(false)}
        />
      )}
      {showAddressModal && (
        <ChangeAddressModal
          worker={worker}
          onClose={() => setShowAddressModal(false)}
          onSuccess={() => {
            setShowAddressModal(false);
            fetchWorkerData();
          }}
        />
      )}
      {showViolationsModal && (
        <ViolationsModal
          workerId={worker.id}
          onClose={() => setShowViolationsModal(false)}
        />
      )}
      {showResolvePrompt && (
        <ConfirmationModal
          acceptAction={handleBulkResolveViolations}
          acceptButtonLabel="Resolve All"
          bodyContentText={`This worker has active violations. Would you like to resolve them all, or review them individually?`}
          denyAction={() => {
            setShowResolvePrompt(false);
            setShowViolationsModal(true);
          }}
          denyButtonLabel="Keep & View"
          hideModal={() => setShowResolvePrompt(false)}
          title="Active Violations Found"
          loading={resolvingViolations}
        />
      )}

      <Stack vertical gap={16}>
        {/* Breadcrumbs */}
        <Stack gap={4} style={{ alignItems: 'center' }}>
          <NavLink
            to="/workers_v2"
            style={{
              color: '#45A735',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            GravyWorkers
          </NavLink>
          <span style={{ color: '#9CA3AF', fontSize: '14px' }}>&gt;</span>
          <span style={{ color: '#6B7280', fontSize: '14px' }}>
            {workerName}&apos;s Profile
          </span>
        </Stack>

        {/* Suspension Banner */}
        <WorkerSuspensionBanner
          worker={worker}
          onLiftSuspension={
            canUnsuspend ? handleOpenLiftSuspensionModal : undefined
          }
          onReactivate={!worker.active ? handleOpenReactivateModal : undefined}
          liftingSuspension={liftingSuspension}
          reactivating={reactivatingWorker}
        />

        {(actionError || actionSuccess) && (
          <Stack vertical gap={8}>
            {actionError && (
              <Alert
                description={actionError}
                icon={faExclamationTriangle}
                status="danger"
                title="Error"
              />
            )}
            {actionSuccess && (
              <Alert
                description={actionSuccess}
                icon={faCheckCircle}
                status="success"
                title="Success"
              />
            )}
          </Stack>
        )}

        {/* Permanent Suspend Modal */}
        {showSuspendModal && (
          <PermanentSuspendModal
            workerId={worker.id}
            workerName={workerName}
            onClose={() => setShowSuspendModal(false)}
            onSuccess={fetchWorkerData}
          />
        )}

        {/* Worker Name Section in Card */}
        <Card css={{ padding: '24px' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{ cursor: 'pointer', flexShrink: 0 }}
              title="Change avatar"
              onClick={() => setShowAvatarModal(true)}
            >
              <Avatar
                firstName={worker.user?.first_name || ''}
                size="lg"
                src={worker.avatar_url || undefined}
              />
            </div>
            <Stack vertical gap={4} style={{ flex: 1, minWidth: '200px' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <Title>{workerName}</Title>
                <Badge
                  label={getAccountStatusLabel(worker)}
                  status={getAccountStatusBadgeStatus(worker)}
                />
                <Badge
                  label={getSuspensionStatusLabel(worker)}
                  status={getSuspensionStatusBadgeStatus(worker)}
                />
                <Badge
                  label={worker.tax_type?.toUpperCase() || 'N/A'}
                  status={worker.tax_type === 'w2' ? 'theme' : 'neutral'}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <Small color="lighter">{worker.user?.email || 'N/A'}</Small>
                {worker.user?.phone_number && (
                  <Small color="lighter">{worker.user.phone_number}</Small>
                )}
              </div>
            </Stack>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {worker.active && !worker.is_suspended && (
                <Button
                  a11yLabel="Suspend Account"
                  appearance="outline"
                  label="Suspend Account"
                  status="danger"
                  onClick={() => setShowSuspendModal(true)}
                />
              )}
              <IconButton
                onClick={() => setShowProfileModal(true)}
                size="small"
                title="Edit profile"
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                size="small"
              >
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setShowAvatarModal(true);
                  }}
                >
                  Change Picture
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setShowResetPasswordModal(true);
                  }}
                >
                  Reset Password
                </MenuItem>
                {worker.active && (
                  <MenuItem onClick={handleOpenDeactivateModal}>
                    Deactivate Worker
                  </MenuItem>
                )}
                <MenuItem onClick={handleShareLink}>Share Link</MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate(`/workers/${workerId}`);
                  }}
                >
                  View in V1
                </MenuItem>
              </Menu>
            </div>
          </div>
        </Card>

        {/* Main Content Grid */}
        <div
          className="worker-detail-grid"
          style={{
            width: '100%',
            display: 'grid',
            gap: '24px',
            gridTemplateAreas: '"primary secondary"',
            gridTemplateRows: 'auto',
            gridTemplateColumns: '1fr 320px',
          }}
        >
          <style>{`
            @media (max-width: 768px) {
              .worker-detail-grid {
                grid-template-areas: "primary" "secondary" !important;
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
          <div style={{ gridArea: 'primary', minWidth: 0 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Overview" />
                <Tab label="Onboarding" />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Stack vertical gap={STACK_GAP}>
                <DependabilityCard
                  score={dependabilityScore}
                  onClick={() => setShowViolationsModal(true)}
                />
                {worker.statuses?.onboarding_status ===
                'onboarding_completed' ? (
                  <JobsListCard perPage={10} workerId={worker.id} />
                ) : (
                  <OnboardingStepsCard workerId={worker.id} />
                )}
                <WorkerSkillsCard workerId={worker.id} />
                <CommentsCard
                  workerId={worker.id}
                  comments={worker.comments || []}
                  onCommentAdded={fetchWorkerData}
                />
              </Stack>
            )}

            {activeTab === 1 && (
              <Stack vertical gap={STACK_GAP}>
                <OnboardingStepsCard workerId={worker.id} />
                <SignedDocumentsCard workerId={worker.id} />
                {externals && (
                  <>
                    <CheckHQCard
                      checkhq={externals.checkhq}
                      workerId={worker.id}
                      onSync={handleSyncComplete}
                    />
                    <ZealCard
                      zeal={externals.zeal}
                      workerId={worker.id}
                      onSync={handleSyncComplete}
                    />
                    <CheckrCard
                      checkr={externals.checkr}
                      workerId={worker.id}
                      onSync={handleSyncComplete}
                    />
                  </>
                )}
              </Stack>
            )}
          </div>
          <div style={{ gridArea: 'secondary' }}>
            <Stack vertical gap={STACK_GAP}>
              <WorkerDetailsCard
                worker={worker}
                checkrCandidateId={externals?.checkr?.candidate_id}
                zealEmployeeId={externals?.zeal?.employee_id}
                checkEmployeeId={
                  externals?.checkhq?.available
                    ? externals.checkhq.employee_id
                    : null
                }
                onEditAddress={() => setShowAddressModal(true)}
              />
            </Stack>
          </div>
        </div>
      </Stack>
    </Page>
  );
};

export default WorkerV2;
